import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'gatsby';
import styles from '../../styles/scss/components/tips/usesSection.module.scss';

const UsesSection = ({ data }) => {
  const rowRefs = useRef([]);
  const countRef = useRef(null);
  // Use a single count state that will increment from 0 to 2000
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  
  useEffect(() => {
    rowRefs.current = rowRefs.current.slice(0, 6);
    
    // Log the data to check its structure
    console.log('Uses section data:', data);
    
    // Initialize the Intersection Observer for the count animation
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (entry.isIntersecting && !hasAnimated) {
            animateCount();
            setHasAnimated(true);
          }
        },
        { threshold: 0.1 } // Trigger when at least 10% of the element is visible
      );
      
      if (countRef.current) {
        observer.observe(countRef.current);
      }
      
      return () => {
        if (countRef.current) {
          observer.unobserve(countRef.current);
        }
      };
    }
  }, [hasAnimated]);
  
  // Function to animate the count from 0 to target with ease-in-out effect
  const animateCount = () => {
    const targetNumber = 2000;
    const duration = 3000; // Animation duration in milliseconds
    
    // Start time for the animation
    const startTime = Date.now();
    
    // Animation frame function with ease-in-out effect
    const animate = () => {
      const now = Date.now();
      const elapsedTime = now - startTime;
      const progress = Math.min(elapsedTime / duration, 1);
      
      // Apply ease-in-out function to the progress
      // This creates a slow start, fast middle, and slow end
      let easedProgress;
      
      // Cubic ease-in-out formula
      if (progress < 0.5) {
        // First half - slow to fast (ease in)
        easedProgress = 4 * progress * progress * progress;
      } else {
        // Second half - fast to slow (ease out)
        const t = progress - 1;
        easedProgress = 1 + 4 * t * t * t;
      }
      
      // Calculate the current count based on the eased progress
      const currentCount = Math.floor(easedProgress * targetNumber);
      
      // Update the count state
      setCount(currentCount);
      
      // Continue animation if not complete
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Ensure we end exactly at the target number
        setCount(targetNumber);
      }
    };
    
    // Start the animation
    requestAnimationFrame(animate);
  };

  return (
    <section className={styles.usesSection}>
      <div className={styles.contentContainer}>
        <Link to="/uses" className={styles.usesLink}>
          <div className={styles.usesAmount} ref={countRef}>
            <div className={styles.countAnimation}>
              <span className={styles.number}>
                {count.toString().padStart(4, '0')}
              </span>
            </div>
          </div>
          <div className={styles.usesAmountLabel}>uses</div>
        </Link>
        
        {/* Uses list */}
        <div className={styles.usesListContainer}>
          {/* Divide the uses into 6 rows */}
          {Array.from({ length: 6 }).map((_, rowIndex) => {
            // Calculate how many items per row (rounded up)
            const itemsPerRow = Math.ceil(data.uses.length / 6);
            // Get the items for this row
            const rowItems = data.uses.slice(
              rowIndex * itemsPerRow,
              (rowIndex + 1) * itemsPerRow
            );
            
            // Determine direction based on row index (even rows go left, odd rows go right)
            const direction = rowIndex % 2 === 0 ? styles.scrollLeft : styles.scrollRight;
            
            return (
              <div 
                key={`row-${rowIndex}`} 
                className={`${styles.usesRow} ${direction}`}
                ref={el => rowRefs.current[rowIndex] = el}
              >
                {/* First copy of the row content */}
                <div className={styles.marqueeContent}>
                  {rowItems.map((use, index) => (
                    <React.Fragment key={`use-${rowIndex}-${index}`}>
                      <Link to="/uses" className={styles.useItem}>{use}</Link>
                      <span className={styles.useDot}></span>
                    </React.Fragment>
                  ))}
                </div>
                
                {/* Second copy for seamless looping */}
                <div className={styles.marqueeContent}>
                  {rowItems.map((use, index) => (
                    <React.Fragment key={`use-dup-${rowIndex}-${index}`}>
                      <Link to="/uses" className={styles.useItem}>{use}</Link>
                      <span className={styles.useDot}></span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default UsesSection;
